// The deploy screen's template list: drawers, hand-arranged order, drag and drop.
//
// Twenty-one templates in one alphabetical list is a page you scan rather than
// read. Alphabetical put "Multi Region Hanger 0.25 (noob)" three rows from
// "Multi Region Hanger 0.25 (public)" and "Multi Region Hanger $1 (public)"
// somewhere else again, because "$" sorts before "0" -- an ordering no operator
// has ever wanted. So the order is now whatever somebody dragged it into, and
// related templates can be filed together in a drawer that closes.
//
// Nothing here touches deployment. A group is a drawer, not a container: it
// carries no settings, deletes without taking its templates with it, and the
// deploy path never reads it.
//
// Drag and drop is the native HTML5 API rather than a library. This is one
// internal page with a couple of dozen rows, and dnd-kit would land in a bundle
// shared with the player-facing app to save about eighty lines.

import {
    faChevronDown,
    faChevronRight,
    faFolder,
    faFolderOpen,
    faGripVertical,
    faPen,
    faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DragEvent, ReactNode, useEffect, useState } from 'react'
import { Template, TemplateGroup } from './templatesApi'

/** What is currently being dragged, if anything. */
type Drag = { kind: 'template' | 'group'; id: number } | null

/** Where it would land if dropped right now. */
type DropAt =
    | { kind: 'template'; id: number; edge: 'above' | 'below' }
    | { kind: 'group'; id: number; edge: 'above' | 'below' }
    /** Into this drawer, at the end. `null` means the ungrouped list. */
    | { kind: 'into'; id: number | null }
    | null

/**
 * Rank a template by which drawer it is in, ungrouped last.
 *
 * Used to keep the flat list consistent with the drawers after every move: a
 * template's group and its position are two facts that can disagree, and a list
 * where they do renders a card outside the drawer it claims to be in.
 */
const rankOf = (groups: TemplateGroup[], template: Template) => {
    const index = groups.findIndex((g) => g.id === template.group_id)
    return index === -1 ? groups.length : index
}

/**
 * Re-group a flat list without disturbing order inside a group.
 *
 * Array.prototype.sort has been stable since ES2019, which is the whole trick:
 * sorting only by drawer leaves every within-drawer position exactly as the
 * operator dropped it, and makes each drawer's templates contiguous so the flat
 * order the server stores and the grouped order the screen renders cannot drift
 * apart.
 */
const regroup = (templates: Template[], groups: TemplateGroup[]) =>
    [...templates].sort((a, b) => rankOf(groups, a) - rankOf(groups, b))

/** A 2px rule showing where the dragged thing would land. */
const DropLine = () => <div className='h-0.5 -my-px rounded bg-violet-400' />

const Grip = ({ onArm, title }: { onArm: (armed: boolean) => void; title: string }) => (
    <span
        // The handle arms the drag; the row carries `draggable`. Making the
        // whole row draggable outright breaks text selection in the expanded
        // form underneath, and makes every stray click feel like a near miss.
        onMouseDown={() => onArm(true)}
        onMouseUp={() => onArm(false)}
        // The row header toggles the card open on click. Without this, picking
        // the handle up and putting it back down expands the card.
        onClick={(e) => e.stopPropagation()}
        title={title}
        className='shrink-0 mt-1 cursor-grab active:cursor-grabbing px-1 -ml-1 text-white/25 hover:text-white/70'
    >
        <FontAwesomeIcon icon={faGripVertical} />
    </span>
)

export const TemplateList = ({
    templates,
    groups,
    canEdit,
    renderTemplate,
    onArrange,
    onCollapse,
    onRenameGroup,
    onDeleteGroup,
}: {
    templates: Template[]
    groups: TemplateGroup[]
    canEdit: boolean
    /** Renders one card. Given the handle to place and whether it is in flight. */
    renderTemplate: (t: Template, dragHandle: ReactNode, dragging: boolean) => ReactNode
    /** Persist a whole new arrangement. */
    onArrange: (templates: Template[], groups: TemplateGroup[]) => void
    /** Persist a drawer's open/closed default. Only called for an executive. */
    onCollapse: (group: TemplateGroup, collapsed: boolean) => void
    onRenameGroup: (group: TemplateGroup, name: string) => void
    onDeleteGroup: (group: TemplateGroup) => void
}) => {
    const [drag, setDrag] = useState<Drag>(null)
    const [dropAt, setDropAt] = useState<DropAt>(null)
    const [armed, setArmed] = useState<string | null>(null)
    const [renaming, setRenaming] = useState<number | null>(null)
    const [draftName, setDraftName] = useState('')

    // Open/closed is kept locally and only *seeded* from the server, so a
    // developer -- who cannot write a group -- can still open a drawer to see
    // what is in it. An executive's toggle is additionally stored, which is
    // what makes it the shared default.
    const [closed, setClosed] = useState<Set<number>>(new Set())
    useEffect(() => {
        setClosed(new Set(groups.filter((g) => g.collapsed).map((g) => g.id)))
    }, [groups])

    const endDrag = () => {
        setDrag(null)
        setDropAt(null)
        setArmed(null)
    }

    /** Which half of a row the pointer is in, so the line lands where it looks. */
    const edgeOf = (e: DragEvent): 'above' | 'below' => {
        const box = e.currentTarget.getBoundingClientRect()
        return e.clientY < box.top + box.height / 2 ? 'above' : 'below'
    }

    const dropTemplateNear = (target: Template, edge: 'above' | 'below') => {
        if (drag?.kind !== 'template' || drag.id === target.id) return
        const moved = templates.find((t) => t.id === drag.id)
        if (!moved) return
        const rest = templates.filter((t) => t.id !== drag.id)
        const at = rest.findIndex((t) => t.id === target.id)
        // Adopting the target's drawer is the whole point of dropping next to
        // it: dragging a card into the middle of a group and having it stay
        // ungrouped is the version of this that looks broken.
        rest.splice(edge === 'above' ? at : at + 1, 0, { ...moved, group_id: target.group_id })
        onArrange(regroup(rest, groups), groups)
    }

    const dropTemplateInto = (groupId: number | null) => {
        if (drag?.kind !== 'template') return
        const moved = templates.find((t) => t.id === drag.id)
        if (!moved || moved.group_id === groupId) return
        const rest = templates.filter((t) => t.id !== drag.id)
        const last = rest.map((t) => t.group_id).lastIndexOf(groupId)
        rest.splice(last === -1 ? rest.length : last + 1, 0, { ...moved, group_id: groupId })
        onArrange(regroup(rest, groups), groups)
    }

    const dropGroupNear = (target: TemplateGroup, edge: 'above' | 'below') => {
        if (drag?.kind !== 'group' || drag.id === target.id) return
        const moved = groups.find((g) => g.id === drag.id)
        if (!moved) return
        const rest = groups.filter((g) => g.id !== drag.id)
        const at = rest.findIndex((g) => g.id === target.id)
        rest.splice(edge === 'above' ? at : at + 1, 0, moved)
        // The templates come along with their drawer, which is what regroup
        // does here -- the drawer moved, so every card in it moved with it.
        onArrange(regroup(templates, rest), rest)
    }

    const startRename = (group: TemplateGroup) => {
        setRenaming(group.id)
        setDraftName(group.name)
    }

    const commitRename = (group: TemplateGroup) => {
        const name = draftName.trim()
        setRenaming(null)
        if (name && name !== group.name) onRenameGroup(group, name)
    }

    // A function returning elements, deliberately not a nested component.
    // Declaring <Row> inside this body gives it a new type identity on every
    // render, so React unmounts and remounts the whole subtree -- which throws
    // away TemplateCard's draft form on every deployment poll and on every
    // frame of a drag. Calling a function inlines the elements instead.
    const renderRow = (template: Template) => {
        const dragging = drag?.kind === 'template' && drag.id === template.id
        const hint = dropAt?.kind === 'template' && dropAt.id === template.id ? dropAt.edge : null

        return (
            <div
                draggable={armed === `t:${template.id}`}
                onDragStart={(e) => {
                    // Every drag event this row raises has to stop here. A row
                    // inside a drawer sits inside that drawer's own drag
                    // handlers, so a bubbling dragstart reached the drawer and
                    // was claimed as "the group is being dragged" -- which
                    // made a card already filed in a group impossible to drag
                    // back out of it, the one move somebody makes to undo a
                    // mistake.
                    e.stopPropagation()
                    // Firefox refuses to start a drag without payload, however
                    // irrelevant; the state above is what actually carries it.
                    e.dataTransfer.setData('text/plain', String(template.id))
                    e.dataTransfer.effectAllowed = 'move'
                    setDrag({ kind: 'template', id: template.id })
                }}
                onDragEnd={endDrag}
                onDragOver={(e) => {
                    if (drag?.kind !== 'template') return
                    e.preventDefault()
                    e.stopPropagation()
                    setDropAt({ kind: 'template', id: template.id, edge: edgeOf(e) })
                }}
                onDrop={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    dropTemplateNear(template, edgeOf(e))
                    endDrag()
                }}
                className='flex flex-col gap-1'
                key={template.id}
            >
                {hint === 'above' && <DropLine />}
                {renderTemplate(
                    template,
                    canEdit ? (
                        <Grip
                            onArm={(on) => setArmed(on ? `t:${template.id}` : null)}
                            title='Drag to reorder, or into a group'
                        />
                    ) : null,
                    dragging
                )}
                {hint === 'below' && <DropLine />}
            </div>
        )
    }

    /**
     * One drawer, or the loose list when `group` is null.
     *
     * The loose list is only given a heading once at least one drawer exists.
     * Labelling everything "Ungrouped" on a page with no groups is chrome that
     * describes nothing.
     */
    /** Same reason as renderRow: a function, not a nested component. */
    const renderDrawer = (group: TemplateGroup | null, items: Template[]) => {
        const id = group?.id ?? null
        const open = group ? !closed.has(group.id) : true
        const isDragging = group != null && drag?.kind === 'group' && drag.id === group.id
        const groupHint =
            group && dropAt?.kind === 'group' && dropAt.id === group.id ? dropAt.edge : null
        const takingDrop = dropAt?.kind === 'into' && dropAt.id === id

        const toggle = () => {
            if (!group) return
            const next = new Set(closed)
            const nowClosed = !next.has(group.id)
            if (nowClosed) next.add(group.id)
            else next.delete(group.id)
            setClosed(next)
            if (canEdit) onCollapse(group, nowClosed)
        }

        return (
            <div
                key={id ?? 'ungrouped'}
                draggable={group != null && armed === `g:${group.id}`}
                onDragStart={(e) => {
                    if (!group) return
                    e.dataTransfer.setData('text/plain', `g:${group.id}`)
                    e.dataTransfer.effectAllowed = 'move'
                    setDrag({ kind: 'group', id: group.id })
                }}
                onDragEnd={endDrag}
                onDragOver={(e) => {
                    if (!drag) return
                    e.preventDefault()
                    if (drag.kind === 'group' && group) {
                        setDropAt({ kind: 'group', id: group.id, edge: edgeOf(e) })
                    } else if (drag.kind === 'template') {
                        // Rows stop propagation, so reaching here means the
                        // pointer is over the drawer itself -- its header, its
                        // padding, or its empty body. All of those mean "put it
                        // in this drawer".
                        setDropAt({ kind: 'into', id })
                    }
                }}
                onDrop={(e) => {
                    e.preventDefault()
                    if (drag?.kind === 'group' && group) dropGroupNear(group, edgeOf(e))
                    else if (drag?.kind === 'template') dropTemplateInto(id)
                    endDrag()
                }}
                className={`flex flex-col gap-2 rounded ${group ? 'border p-2' : ''} ${
                    isDragging
                        ? 'opacity-40 border-violet-400/60'
                        : takingDrop
                          ? 'border border-violet-400 bg-violet-500/5'
                          : group
                            ? 'border-white/10 bg-white/[0.02]'
                            : ''
                }`}
            >
                {groupHint === 'above' && <DropLine />}

                {group ? (
                    <div className='flex flex-row items-start gap-2 min-w-0'>
                        {canEdit && (
                            <Grip
                                onArm={(on) => setArmed(on ? `g:${group.id}` : null)}
                                title='Drag to reorder this group'
                            />
                        )}
                        <button
                            onClick={toggle}
                            className='flex flex-row items-center gap-2 min-w-0 grow text-left'
                        >
                            <FontAwesomeIcon
                                icon={open ? faChevronDown : faChevronRight}
                                className='opacity-40 w-3 shrink-0'
                            />
                            <FontAwesomeIcon
                                icon={open ? faFolderOpen : faFolder}
                                className='shrink-0 text-amber-400/70'
                            />
                            {renaming === group.id ? (
                                <input
                                    autoFocus
                                    value={draftName}
                                    onChange={(e) => setDraftName(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onBlur={() => commitRename(group)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') commitRename(group)
                                        if (e.key === 'Escape') setRenaming(null)
                                    }}
                                    className='input-team-tools py-0.5 px-2 text-sm'
                                />
                            ) : (
                                // Stored case, not uppercase: a group name is
                                // data an operator typed, not chrome.
                                <span className='font-bold break-words min-w-0'>{group.name}</span>
                            )}
                            <span className='shrink-0 text-[11px] uppercase tracking-widest text-white/40'>
                                {items.length} template{items.length === 1 ? '' : 's'}
                                {!open && items.length > 0 && ' · closed'}
                            </span>
                        </button>
                        {canEdit && renaming !== group.id && (
                            <div className='flex flex-row gap-2 shrink-0'>
                                <button
                                    onClick={() => startRename(group)}
                                    title='Rename this group'
                                    className='text-white/40 hover:text-white'
                                >
                                    <FontAwesomeIcon icon={faPen} />
                                </button>
                                <button
                                    onClick={() => onDeleteGroup(group)}
                                    title='Delete this group; its templates become ungrouped'
                                    className='text-white/40 hover:text-red-400'
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    groups.length > 0 && (
                        <span className='text-xs uppercase tracking-widest text-white/40'>
                            Ungrouped ({items.length})
                        </span>
                    )
                )}

                {open && (
                    <div className='flex flex-col gap-2'>
                        {items.map((t) => renderRow(t))}
                        {/* An empty drawer still needs somewhere to aim at, or
                            the only way to fill it is to drop next to a card
                            that is not there. */}
                        {!items.length && (
                            <div className='rounded border border-dashed border-white/15 py-3 text-center text-[11px] uppercase tracking-widest text-white/30'>
                                {drag?.kind === 'template'
                                    ? 'drop here'
                                    : group
                                      ? 'empty group'
                                      : 'nothing ungrouped'}
                            </div>
                        )}
                    </div>
                )}

                {groupHint === 'below' && <DropLine />}
            </div>
        )
    }

    const ungrouped = templates.filter((t) => !t.group_id)

    return (
        <div className='flex flex-col gap-3'>
            {groups.map((g) =>
                renderDrawer(
                    g,
                    templates.filter((t) => t.group_id === g.id)
                )
            )}
            {/* Rendered even when empty once drawers exist: it is the only
                target for dragging a template back out of one. */}
            {(ungrouped.length > 0 || groups.length > 0) && renderDrawer(null, ungrouped)}
        </div>
    )
}
